import { createElement } from 'react';

import type { SidebarRitualRow, SidebarViewRow } from '@flow/sync';
import type { SidebarSection } from '@flow/ui/components';

import { t } from '../i18n/es.js';

import { RitualIcon, ViewIcon } from './icons.jsx';

/**
 * Turns the installed views and rituals into the sidebar sections (docs/05 §3). The order is the
 * one the template stored (`sort_key`), the section is the one each view carries, and views
 * without a section (the helpers of the weekly review) stay out of the sidebar.
 */

/** Sections in the order of docs/05 §3; a view with an unknown section is ignored. */
const SECTION_ORDER = ['flow', 'organization'] as const;
export type SidebarSectionKey = (typeof SECTION_ORDER)[number];

export const SECTION_TITLES: Record<string, string> = {
  flow: t.shell.sections.flow,
  organization: t.shell.sections.organization,
  rituals: t.shell.sections.rituals,
};

export function viewHref(key: string): string {
  return `/v/${key}`;
}

/** The Trash is not a view with a filter: a fixed entry at the end of «Organización» (D8). */
export const TRASH_HREF = '/trash';

export interface SidebarSectionsInput {
  views: SidebarViewRow[];
  rituals: SidebarRitualRow[];
  badges: Record<string, string>;
  warningViews: string[];
  isCurrent: (href: string) => boolean;
}

export function sidebarSections({
  views,
  rituals,
  badges,
  warningViews,
  isCurrent,
}: SidebarSectionsInput): SidebarSection[] {
  const sections: SidebarSection[] = [];

  for (const section of SECTION_ORDER) {
    const items = views
      .filter((view) => view.sidebar_section === section && view.key)
      .map((view) => {
        const key = view.key!;
        const href = viewHref(key);
        const badge = badges[key];
        return {
          key,
          name: view.name,
          icon: createElement(ViewIcon, { name: view.icon }),
          href,
          current: isCurrent(href),
          ...(badge === undefined ? {} : { badge }),
          ...(badge !== undefined && warningViews.includes(key)
            ? { badgeIsWarning: true, badgeDescription: t.shell.overdue(badge) }
            : {}),
        };
      });
    if (items.length > 0) {
      sections.push({ key: section, title: SECTION_TITLES[section] ?? section, items });
    }
  }

  // The Trash closes «Organización», with no counter (f1-trash-search D8).
  const trash = {
    key: 'trash',
    name: t.trash.title,
    icon: createElement(ViewIcon, { name: 'trash' }),
    href: TRASH_HREF,
    current: isCurrent(TRASH_HREF),
  };
  const organization = sections.find((section) => section.key === 'organization');
  if (organization) organization.items.push(trash);
  else
    sections.push({
      key: 'organization',
      title: SECTION_TITLES.organization ?? '',
      items: [trash],
    });

  if (rituals.length > 0) {
    sections.push({
      key: 'rituals',
      title: SECTION_TITLES.rituals ?? 'Rituales',
      items: rituals.map((ritual) => ({
        key: ritual.key,
        name: ritual.name,
        icon: createElement(RitualIcon),
        // The ritual screen arrives in F2; until then it points at its own route, which explains it.
        href: `/r/${ritual.key}`,
        current: isCurrent(`/r/${ritual.key}`),
      })),
    });
  }

  return sections;
}
